import React, {createContext, useContext, useMemo} from 'react';

import {SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {SettingOverrideCounts} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {DispatchableAction} from '../../../../types/redux';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {LinePlotSettings} from '../types';

export type LinePlotSettingsContextType = {
  clearSetting: ClearSettingFnType;
  /**
   * These are the values used in the settings UX. This is the resolved value between different levels of settings.
   */
  displayLinePlotSettings: LinePlotSettings | undefined;
  /**
   * These are the settings for the specified settingLevel. This should match what's in the view spec, but not necessarily
   * be the value that is displayed depending on the settingLevel
   */
  linePlotSettings?: LinePlotSettings;
  settingLevel: 'workspace' | 'section';
  settingOverrideCounts: SettingOverrideCounts;
  trackLinePlotSetting: (
    setting: SettingType,
    value: string | undefined
  ) => void;
  updateLinePlotSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
};

export const LinePlotSettingsContext = createContext<
  LinePlotSettingsContextType | undefined
>(undefined);

type LinePlotSettingsProps = LinePlotSettingsContextType & {
  children: React.ReactNode;
};

export const LinePlotSettingsContextProvider = ({
  children,
  clearSetting,
  displayLinePlotSettings,
  linePlotSettings,
  settingLevel,
  settingOverrideCounts,
  trackLinePlotSetting,
  updateLinePlotSettings,
}: LinePlotSettingsProps) => {
  const value = useMemo(
    () => ({
      clearSetting,
      displayLinePlotSettings,
      linePlotSettings,
      settingLevel,
      settingOverrideCounts,
      trackLinePlotSetting,
      updateLinePlotSettings,
    }),
    [
      clearSetting,
      displayLinePlotSettings,
      linePlotSettings,
      settingLevel,
      settingOverrideCounts,
      trackLinePlotSetting,
      updateLinePlotSettings,
    ]
  );

  return (
    <LinePlotSettingsContext.Provider value={value}>
      {children}
    </LinePlotSettingsContext.Provider>
  );
};

export const useLinePlotSettingsContext = (): LinePlotSettingsContextType => {
  const value = useContext(LinePlotSettingsContext);

  if (value == null) {
    throw new Error(
      'useLinePlotSettingsContext must be used within a LinePlotSettingsContextProvider'
    );
  }

  return value;
};
